"use client";

import { Meeting } from "@/api/calendar";
import { Button } from "@/components/ui/button";
import { formatUTCToLocal } from "@/utils/calendar";
import {
  Building,
  Calendar,
  Clock,
  Edit,
  MapPin,
  ArrowRightLeft,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useState } from "react";
import { DeleteDialog } from "@/components/delete-dialog";
import { Link } from "@tanstack/react-router";
import { usePermission } from "@/hooks/use-permission";
import { UserPermission } from "@/constants/permissions";

interface EventModalProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: any;
  onClose: () => void;
  onEdit?: (meeting: Meeting) => void;
  onDelete?: (id: string) => void;
  onPostpone?: (meeting: Meeting) => void;
}

export function EventModal({
  event,
  onClose,
  onEdit,
  onDelete,
  onPostpone,
}: EventModalProps) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEventModal, setShowEventModal] = useState(true);
  const { can, user } = usePermission();

  if (!event) return null;
  const meeting = event.originalData;

  const isOwner = user?.id === meeting.assigned_to?.id || user?.id === meeting.added_by?.id;
  const isCaseOwner = meeting.case && (user?.id === meeting.case.assigned_to?.id || user?.id === meeting.case.added_by?.id);

  const canEdit = can(UserPermission.CHANGE_RENDEZVOUS) || (can(UserPermission.CHANGE_OWN_RENDEZVOUS) && isOwner);
  const canDelete = can(UserPermission.DELETE_RENDEZVOUS) || (can(UserPermission.DELETE_OWN_RENDEZVOUS) && isOwner);
  const canViewCase = can(UserPermission.VIEW_CASE) || (can(UserPermission.VIEW_OWN_CASE) && isCaseOwner);

  const formatTime = (date: number) => {
    return formatUTCToLocal(new Date(date * 1000));
  };

  const formatDay = (date: number) => {
    return formatUTCToLocal(new Date(date * 1000), 'EEEE d/M');
  };

  const isCourt = meeting.tribunal_proc_type !== undefined
    && meeting.tribunal_proc_type !== null
    && meeting.tribunal_proc_type === true;

  const handleDeleteClick = () => {
    setShowEventModal(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteSuccess = () => {
    // Don't reopen the event modal after successful deletion
    setShowDeleteConfirm(false);
  };

  // Function for the DeleteDialog component to delete the meeting
  const deleteRow = async (): Promise<void> => {
    if (meeting?.id && onDelete) {
      onDelete(meeting.id);
      return Promise.resolve();
    }
    return Promise.reject(new Error("Failed to delete"));
  };

  // Function to get the name of the meeting for the delete confirmation
  const getDeleteName = () => {
    return event.title || t("appointment");
  };



  return (
    <>
      <Dialog open={showEventModal && !!event} onOpenChange={(open) => {
        if (!open) onClose();
        setShowEventModal(open);
      }}>
        <DialogContent
          className="sm:max-w-md max-h-[90vh] overflow-y-auto"
          style={{
            borderRight: `4px solid ${event.color}`,
          }}
          dir="rtl"
        >
          <DialogTitle className="sr-only">{event.title}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('event_details_description')}
          </DialogDescription>
          <h3 className="mb-4 text-2xl font-bold text-gray-800 dark:text-gray-100">
            {event.title}
          </h3>
          {meeting.postponed_from !== null && (
            <div className="mt-2 px-2 py-1 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded text-red-700 dark:text-red-400">
              <p className="flex items-center">
                <ArrowRightLeft className="w-4 h-4 ml-2" />
                {t("meetingPostponedWarning")}
              </p>
            </div>
          )}
          {meeting.is_postponed && (
            <div className="mt-2 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-800 rounded text-amber-700 dark:text-amber-400">
              {t("meetingPostponedToNewTime")}
            </div>
          )}
          <div className="space-y-3 text-gray-700 dark:text-gray-300">
            <p className="flex items-center">
              <Calendar className="w-5 h-5 ml-2 text-gray-500 dark:text-gray-400" />
              {meeting.postponed_from ? (
                <>
                  <span className="line-through text-red-500 dark:text-red-400">
                    {formatDay(meeting.postponed_from.start_time)}
                  </span>
                  <span className="mx-1">←</span>
                </>
              ) : null}
              {meeting.is_postponed ? (
                <span className="line-through text-amber-500 dark:text-amber-400">
                  {formatDay(meeting.start_time)}
                </span>
              ) : (
                formatDay(meeting.start_time)
              )}
            </p>
            <p className="flex items-center">
              <Clock className="w-5 h-5 ml-2 text-gray-500 dark:text-gray-400" />
              {meeting.postponed_from ? (
                <>
                  <span className="line-through text-red-500 dark:text-red-400">
                    {formatTime(meeting.postponed_from.start_time)}
                    {meeting.postponed_from.end_time && (
                      <>
                        {" "}-{" "}
                        {formatTime(meeting.postponed_from.end_time)}
                      </>
                    )}
                  </span>
                  <span className="mx-1">←</span>
                </>
              ) : null}
              {meeting.is_postponed ? (
                <span className="line-through text-amber-500 dark:text-amber-400">
                  {formatTime(meeting.start_time)}
                  {meeting.end_time && (
                    <>
                      {" "}-{" "}
                      {formatTime(meeting.end_time)}
                    </>
                  )}
                </span>
              ) : (
                <>
                  {formatTime(meeting.start_time)}
                  {meeting.end_time && (
                    <>
                      {" "}-{" "}
                      {formatTime(meeting.end_time)}
                    </>
                  )}
                </>
              )}
            </p>

            {meeting.case && (
              <div className="flex items-center">
                <MapPin className="w-5 h-5 ml-2 text-gray-500 dark:text-gray-400" />
                {t("case")}:
                <span className="px-2 flex gap-2">
                  {canViewCase ? (
                    <Link
                      to="/cases/$id"
                      params={{ id: meeting.case.id }}
                      className='w-fit text-nowrap hover:underline'
                    >
                      {meeting.case.name}
                    </Link>
                  ) : (
                    <span>{meeting.case.name}</span>
                  )}
                  <Badge
                    variant="default"
                  >
                    {meeting.case.id}
                  </Badge>
                </span>
              </div>
            )}

            <p className="flex items-center">
              <Building className="w-5 h-5 ml-2 text-gray-500 dark:text-gray-400" />
              {t("meetingType")}: {isCourt ? t("court") : t("office")}
            </p>

            <p className="flex items-center">
              <Users className="w-5 h-5 ml-2 text-gray-500 dark:text-gray-400" />
              {t("assigned_to")}:
              {can(UserPermission.VIEW_USER) ? (
                <Link
                  to="/users/$id"
                  params={{ id: meeting.assigned_to.id }}
                  className='w-fit text-nowrap hover:underline'
                >
                  {meeting.assigned_to.name}
                </Link>
              ) : (
                <span>{meeting.assigned_to.name}</span>
              )}
            </p>
            <p className="flex items-center">
              <UserPlus className="w-5 h-5 ml-2 text-gray-500 dark:text-gray-400" />
              {t("added_by")}:
              {can(UserPermission.VIEW_USER) ? (
                <Link
                  to="/users/$id"
                  params={{ id: meeting.added_by.id }}
                  className='w-fit text-nowrap hover:underline'
                >
                  {meeting.added_by.name}
                </Link>
              ) : (
                <span>{meeting.added_by.name}</span>
              )}
            </p>

            <p>
              <strong>{t("created_at")}:</strong>{" "}
              {formatUTCToLocal(new Date(meeting.created_at * 1000), "HH:mm EEEE d/M/y")}
            </p>
          </div>
          <div className="flex flex-col gap-2 mt-6 sm:flex-row sm:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto">
              {canDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteClick}
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="w-4 h-4 ml-2" />
                  {t("delete")}
                </Button>
              )}

              {canEdit && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (onEdit) {
                        onEdit(meeting);
                      }
                    }}
                    className="w-full sm:w-auto"
                  >
                    <Edit className="w-4 h-4 ml-2" />
                    {t("edit")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (onPostpone) {
                        onPostpone(meeting);
                      }
                    }}
                    className="w-full sm:w-auto"
                  >
                    <ArrowRightLeft className="w-4 h-4 ml-2" />
                    {t("postpone")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {can(UserPermission.DELETE_RENDEZVOUS) && (
        <DeleteDialog
          open={showDeleteConfirm}
          onOpenChange={(open) => {
            setShowDeleteConfirm(open);
            if (!open) {
              setShowEventModal(true); // Reopen event modal when deletion dialog is closed without taking action
            }
          }}
          onDeleted={handleDeleteSuccess}
          getDeleteName={getDeleteName}
          deleteRow={deleteRow}
          deleteTitle={t("delete")}
        />
      )}
    </>
  );
}
